import React, { useEffect, useMemo, useState } from 'react';
import { getFullPromptHistory, FullPromptHistory, FullPromptVersion } from '../lib/api';
import { I, Station } from './Shared';

export function FullPromptHistoryModal({
  projectId,
  appName,
  onClose,
}: {
  projectId: string;
  appName: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<FullPromptHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await getFullPromptHistory(projectId);
        if (!cancelled) setData(result);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'failed to load prompt history');
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const stepPromptCount = useMemo(
    () => (data ? data.steps.reduce((n, s) => n + s.versions.length, 0) : 0),
    [data],
  );
  const hasIdea = !!data?.project.idea && data.project.idea.trim().length > 0;
  const totalPrompts = stepPromptCount + (hasIdea ? 1 : 0);

  function toggle(key: string) {
    setCollapsed((c) => ({ ...c, [key]: !c[key] }));
  }

  function handleDownload() {
    if (!data) return;
    const md = renderMarkdown(data);
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slugify(appName || data.project.appName)}-prompts-${todayStamp()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="hm-backdrop" onClick={onClose}>
      <div
        className="af card"
        style={{
          width: 920,
          height: '88%',
          maxHeight: 820,
          padding: 0,
          background: 'var(--surface)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px var(--line)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span className="loz">PROMPT HISTORY</span>
          <span className="t-h2" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {appName}
          </span>
          <span style={{ flex: 1 }} />
          {data && (
            <span className="t-micro muted">
              {totalPrompts} prompt{totalPrompts === 1 ? '' : 's'} · {data.steps.length} step
              {data.steps.length === 1 ? '' : 's'}
            </span>
          )}
          <button
            className="btn btn--sm"
            onClick={handleDownload}
            disabled={!data || totalPrompts === 0}
            title="Download as Markdown"
          >
            {I.download} Download
          </button>
          <button className="btn btn--ghost btn--sm btn--icon" onClick={onClose}>{I.x}</button>
        </div>

        {error && <div style={{ padding: 16, color: 'var(--err)' }}>{error}</div>}

        {!error && !data && (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-muted)' }}>Loading…</div>
        )}

        {data && !hasIdea && data.steps.length === 0 && (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-muted)' }}>
            No user prompts recorded yet for this app.
          </div>
        )}

        {data && (hasIdea || data.steps.length > 0) && (
          <div className="scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '14px 18px 20px' }}>
            {hasIdea && (
              <div className="card" style={{ marginBottom: 12, padding: 0, overflow: 'hidden' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 14px',
                    background: 'var(--surface-2)',
                    borderBottom: '1px solid var(--line)',
                  }}
                >
                  <span className="loz loz--accent">INITIAL IDEA</span>
                  <span className="t-h3">The original prompt that started this app</span>
                  <span style={{ flex: 1 }} />
                  <span className="t-micro muted">
                    {new Date(data.project.createdAt).toLocaleString()}
                  </span>
                </div>
                <div style={{ padding: '12px 14px' }}>
                  <div
                    style={{
                      padding: '10px 12px',
                      background: 'var(--surface-2)',
                      borderLeft: '2px solid var(--accent)',
                      borderRadius: 3,
                      fontSize: 13,
                      lineHeight: 1.55,
                      color: 'var(--ink-2)',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {data.project.idea}
                  </div>
                </div>
              </div>
            )}
            {data.steps.map((step) => {
              const isCollapsed = !!collapsed[step.key];
              return (
                <div
                  key={step.key}
                  className="card"
                  style={{ marginBottom: 12, padding: 0, overflow: 'hidden' }}
                >
                  <button
                    onClick={() => toggle(step.key)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '12px 14px',
                      background: 'var(--surface-2)',
                      border: 'none',
                      borderBottom: isCollapsed ? 'none' : '1px solid var(--line)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      color: 'inherit',
                    }}
                  >
                    <span style={{ color: 'var(--ink-faint)' }}>{isCollapsed ? I.chevR : I.chevD}</span>
                    <span className="t-h3">{step.label}</span>
                    <span className="t-micro muted">{step.phaseLabel}</span>
                    <span style={{ flex: 1 }} />
                    <span className="t-micro muted">
                      {step.versions.length} prompt{step.versions.length === 1 ? '' : 's'}
                    </span>
                  </button>
                  {!isCollapsed && (
                    <div style={{ padding: '10px 14px 14px' }}>
                      {step.versions.map((v) => (
                        <PromptEntry key={v.versionId} entry={v} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--line)',
            background: 'var(--bg-sunken)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span className="t-small muted">All user prompts that shaped this app, newest first per step.</span>
          <span style={{ flex: 1 }} />
          <button className="btn btn--sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function PromptEntry({ entry }: { entry: FullPromptVersion }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        padding: '10px 0',
        borderBottom: '1px solid var(--line)',
      }}
    >
      <Station n={entry.version} size="sm" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span className="t-h3">v{entry.version}</span>
          <span className="t-small" style={{ color: 'var(--ink-2)' }}>{causeLabel(entry.cause)}</span>
          <span style={{ flex: 1 }} />
          <span className="t-micro muted">{new Date(entry.createdAt).toLocaleString()}</span>
        </div>
        <div
          style={{
            padding: '8px 10px',
            background: 'var(--surface-2)',
            borderLeft: '2px solid var(--accent)',
            borderRadius: 3,
            fontSize: 12.5,
            lineHeight: 1.55,
            color: 'var(--ink-2)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {entry.prompt}
        </div>
      </div>
    </div>
  );
}

function causeLabel(cause: string): string {
  if (cause === 'bootstrap') return 'Initial idea';
  if (cause.startsWith('edit:')) return 'Improvement request';
  if (cause.startsWith('answer:')) return 'Answers submitted';
  if (cause.startsWith('rerun:')) return 'Re-run';
  if (cause.startsWith('awaiting-input:')) return 'Awaiting input';
  return cause;
}

function renderMarkdown(data: FullPromptHistory): string {
  const lines: string[] = [];
  lines.push(`# ${data.project.appName} — prompt history`);
  lines.push('');
  lines.push(`Exported ${new Date().toISOString()}`);
  lines.push(`Project created ${new Date(data.project.createdAt).toISOString()}`);
  lines.push('');
  if (data.project.idea) {
    lines.push('## Original idea');
    lines.push('');
    lines.push('```');
    lines.push(data.project.idea);
    lines.push('```');
    lines.push('');
  }
  for (const step of data.steps) {
    lines.push(`## ${step.label} (${step.key})`);
    lines.push(`_Phase: ${step.phaseLabel}_`);
    lines.push('');
    for (const v of step.versions) {
      lines.push(
        `### Version ${v.version} — ${causeLabel(v.cause)} — ${new Date(v.createdAt).toISOString()}`,
      );
      lines.push('');
      lines.push('```');
      lines.push(v.prompt ?? '');
      lines.push('```');
      lines.push('');
    }
  }
  return lines.join('\n');
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'app';
}

function todayStamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}
